import { useState, useEffect, useCallback } from "react";
import {
  FetchFromServer,
  InsertToServer,
  UpdateToServer,
  DeleteFromServer,
} from "../../../api/ServerApi";

function useSidebarDomain(selectedPanel) {
  /* ================= Manage users ================= */

  const [showManageUsersPanel, setShowManageUsersPanel] = useState(false);

  /* ================= USER SETTINGS ================= */
  const [showUserPanel, setShowUserPanel] = useState(false);
  const [userList, setUserList] = useState([]);
  const [userLoading, setUserLoading] = useState(false);

  const [showUserSettingsPanel, setShowUserSettingsPanel] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  /* ================= Marathi Words ================= */
  const [showMarathiPanel, setShowMarathiPanel] = useState(false);
  const [marathiList, setMarathiList] = useState([]);
  const [marathiLoading, setMarathiLoading] = useState(false);

  /* ================= English Words ================= */
  const [showEnglishPanel, setShowEnglishPanel] = useState(false);
  const [englishList, setEnglishList] = useState([]);
  const [englishLoading, setEnglishLoading] = useState(false);
  const [selectedMarathiWord, setSelectedMarathiWord] = useState("");

  /* ================= Editing / Selection ================= */
  const [editingId, setEditingId] = useState(null);
  const [editedValue, setEditedValue] = useState("");
  const [selectedEnglishRowId, setSelectedEnglishRowId] = useState(null);

  /* ================= Modals ================= */
  const [showAddModal, setShowAddModal] = useState(false);
  const [activationCode, setActivationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [showMarathiWordsPage, setShowMarathiWordsPage] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  /* ================= SUPER ADMIN ================= */
  const [showSuperAdminPanel, setShowSuperAdminPanel] = useState(false);

  /* ================= DEBUG TOAST ================= */
  const [showDebugToast, setShowDebugToast] = useState(false);
  const [debugInfo, setDebugInfo] = useState({
    api: "",
    payload: null,
    response: null,
    error: null,
  });

  const panelLeftOffset = 0;

  const PANEL_COLORS = {
    marathiList: "#e8f4ff",
    englishList: "#e9fbe9",
    userList: "#fff9e6",
    userSettings: "#f4ecff",
    superAdmin: "#f2f2f2",
    marathiFull: "#fff1e6",
  };

  const sidebarColor = selectedPanel
    ? `rgb(${selectedPanel.Fore_RedCPer}, ${selectedPanel.Fore_GreenPer}, ${selectedPanel.Fore_BluePer})`
    : "#2c3e50";

  const isSuperAdmin = localStorage.getItem("SuperAdmin") === "1";
  const loggedInUserAllowSettings =
    localStorage.getItem("AllowSettings") === "1";
  const canAccessUserSettings =
    isSuperAdmin || loggedInUserAllowSettings;

  /* ================= MARATHI LIST ================= */
  const fetchMarathiWordsList = async () => {
    try {
      setMarathiLoading(true);

      setShowMarathiWordsPage(false);
      setShowEnglishPanel(false);
      setEnglishList([]);
      setSelectedMarathiWord("");
      setSelectedEnglishRowId(null);

      const data = await FetchFromServer(
        "GetListOfEnglishWordsWithSameMarathiWords",
        {}
      );

      setMarathiList(Array.isArray(data) ? data : []);
      setShowMarathiPanel(true);
    } catch (err) {
      console.error(err);
      alert("Failed to load Marathi words list");
    } finally {
      setMarathiLoading(false);
    }
  };

  /* ================= TOGGLE USER ACTIVE ================= */
  const toggleUserActive = async (user) => {
    let payload = null;

    try {
      const newStatus = Number(user.InactiveUser) === 1 ? 0 : 1;

      payload = {
        data: { InactiveUser: newStatus },
        where: `UserCorpPanelCandidateMappingId = ${Number(
          user.UserCorpPanelCandidateMappingId
        )}`,
      };

      const response = await UpdateToServer(
        "UpdateUserCorpCandidatePanelMapping",
        payload
      );

      setDebugInfo({
        api: "UpdateUserCorpCandidatePanelMapping",
        payload,
        response,
        error: null,
      });
      setShowDebugToast(false);

      setUserList((prev) =>
        prev.map((u) =>
          u.UserCorpPanelCandidateMappingId ===
          user.UserCorpPanelCandidateMappingId
            ? { ...u, InactiveUser: newStatus }
            : u
        )
      );
    } catch (err) {
      setDebugInfo({
        api: "UpdateUserCorpCandidatePanelMapping",
        payload,
        response: null,
        error: err?.message || err,
      });
      setShowDebugToast(false);
      alert("Failed to update user status");
    }
  };

  /* ================= USER LIST ================= */
  const fetchUsersList = async () => {
    try {
      if (!selectedPanel) {
        alert("Please select a panel first");
        return;
      }

      setUserLoading(true);

      const payload = {
        CorporationId: Number(selectedPanel.CorporationId),
        CandidateId: Number(selectedPanel.CandidateId),
        PanelNumber: Number(selectedPanel.PanelNumber),
      };

      const data = await FetchFromServer(
        "GetAllUsersByCorporationIdAndPanelNumberAndCandidateId",
        payload,
        { plain: true }
      );

      setDebugInfo({
        api: "GetAllUsersByCorporationIdAndPanelNumberAndCandidateId",
        payload,
        response: data,
        error: null,
      });
      setShowDebugToast(false);

      const users =
        Array.isArray(data) &&
        data.length > 0 &&
        Array.isArray(data[0].Users)
          ? data[0].Users
          : [];

      setUserList(users);
      setShowUserPanel(true);
    } catch (err) {
      setDebugInfo({
        api: "GetAllUsersByCorporationIdAndPanelNumberAndCandidateId",
        payload: selectedPanel,
        response: null,
        error: err?.message || err,
      });
      setShowDebugToast(false);
      alert("Failed to load users");
    } finally {
      setUserLoading(false);
    }
  };

  /* ================= ENGLISH LIST ================= */
  const fetchEnglishWords = async (marathiWord) => {
    try {
      setEnglishLoading(true);
      setSelectedMarathiWord(marathiWord);

      const data = await FetchFromServer(
        "GetEnglishWordsListByMarathiWords",
        { MarathiWords: marathiWord }
      );

      setEnglishList(Array.isArray(data) ? data : []);
      setSelectedEnglishRowId(null);
      setShowEnglishPanel(true);
    } catch {
      alert("Failed to load English words");
    } finally {
      setEnglishLoading(false);
    }
  };

  /* ================= MODALS ================= */
  const handleOpenAddModal = () => setShowAddModal(true);

  const handleCloseAddModal = () => {
    setActivationCode("");
    setShowAddModal(false);
  };

  const handleActivationSubmit = async () => {
    if (!/^\d{4}$/.test(activationCode)) {
      alert("Activation code must be exactly 4 numeric digits");
      return;
    }

    setLoading(true);
    try {
      const data = await FetchFromServer(
        "GetCandidateDetailsForCorporationByDataPassword",
        { DataPassword: activationCode }
      );

      if (!Array.isArray(data) || data.length === 0) {
        alert("No candidate found");
        return;
      }

      const userId = Number(localStorage.getItem("UserId"));

      await Promise.all(
        data.map((item) =>
          InsertToServer(
            "AddUserCorpCandidatePanelMapping",
            {
              UserId: userId,
              CorpCandidateMappingId: item.CorpCandidateMappingId,
            },
            { plain: true }
          )
        )
      );

      alert("Constitution added successfully!");
      handleCloseAddModal();
    } catch {
      alert("Failed to add constitution");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);

    if (!selectedPanel) {
      alert("Please select a panel first");
      return;
    }

    setLoading(true);

    try {
      const userId = Number(localStorage.getItem("UserId"));

      // `deleteUserCorpCandidatePanelMapping` only exists under /api/, not
      // /apisecure/, so calling it through ServerApi 404'd and Remove Panel
      // silently did nothing. The /apisecure/ route below takes the three ids
      // directly and resolves CorpCandidateMappingId server-side.
      const payload = {
        UserId: userId,
        CorporationId: Number(selectedPanel.CorporationId),
        PanelNumber: Number(selectedPanel.PanelNumber),
      };

      await FetchFromServer(
        "deleteUserCorpCandidatePanelMappingByCorporationIdAndPanelNumberAndUserId",
        payload,
        { plain: true }
      );

      window.location.reload();
    } catch (err) {
      console.error("Remove panel failed", err);
      alert("Failed to remove panel");
    } finally {
      setLoading(false);
    }
  };

  /* ================= AUTO SAVE ================= */
  const autoSaveEnglishWord = async (row) => {
    if (isSaving) return;
    if (editingId !== row.MarathiWordId) return;

    const newValue = editedValue.trim();
    if (!newValue || newValue === row.EnglishWords) {
      setEditingId(null);
      return;
    }

    try {
      setIsSaving(true);

      await UpdateToServer(
        "Updatemarathiwords",
        {
          data: { EnglishWords: newValue },
          where: `MarathiWordId = ${row.MarathiWordId}`,
        }
      );

      setEnglishList((prev) =>
        prev.map((r) =>
          r.MarathiWordId === row.MarathiWordId
            ? { ...r, EnglishWords: newValue }
            : r
        )
      );
    } finally {
      setIsSaving(false);
      setEditingId(null);
      setEditedValue("");
    }
  };

  /* ================= DELETE OTHERS ================= */
  const handleDeleteExceptSelected = useCallback(async () => {
    if (!selectedEnglishRowId) return;

    const idsToDelete = englishList
      .filter((r) => r.MarathiWordId !== selectedEnglishRowId)
      .map((r) => r.MarathiWordId);

    if (!idsToDelete.length) return;

    await DeleteFromServer(
      "deletemarathiwords",
      {
        table: "marathiwords",
        where: `MarathiWordId IN (${idsToDelete.join(",")})`,
      },
      { plain: true }
    );

    setEnglishList((prev) =>
      prev.filter((r) => r.MarathiWordId === selectedEnglishRowId)
    );
  }, [englishList, selectedEnglishRowId]);

  /* ================= F10 KEY ================= */
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!showEnglishPanel || !selectedEnglishRowId) return;

      if (e.key === "F10") {
        e.preventDefault();
        handleDeleteExceptSelected();
        setShowEnglishPanel(false);
        setEnglishList([]);
        setSelectedMarathiWord("");
        setSelectedEnglishRowId(null);
        fetchMarathiWordsList();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showEnglishPanel, selectedEnglishRowId, handleDeleteExceptSelected]);

  /* ================= PRINTER ================= */
  const handlePrinterSetup = () => {
    alert("After download, run as Administrator.");

    const url =
      "http://smartifyindia.co.in/uploads/exe/Normal POS Driver VL2.0.3.exe";

    const link = document.createElement("a");
    link.href = url;
    link.download = "Normal_POS_Driver_VL2.0.3.exe";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return {
    showManageUsersPanel, setShowManageUsersPanel,
    showUserPanel,
    setShowUserPanel,
    userList,
    showUserSettingsPanel,
    setShowUserSettingsPanel,
    selectedUser,
    setSelectedUser,

    showMarathiPanel,
    setShowMarathiPanel,
    marathiList,
    marathiLoading,

    showEnglishPanel,
    setShowEnglishPanel,
    englishList,
    setEnglishList,
    selectedMarathiWord,
    setSelectedMarathiWord,

    editingId,
    setEditingId,
    editedValue,
    setEditedValue,
    selectedEnglishRowId,
    setSelectedEnglishRowId,

    showAddModal,
    setShowAddModal,
    activationCode,
    setActivationCode,
    loading,

    showMarathiWordsPage,
    setShowMarathiWordsPage,
    showDeleteConfirm,
    setShowDeleteConfirm,

    showSuperAdminPanel,
    setShowSuperAdminPanel,

    showDebugToast,
    setShowDebugToast,
    debugInfo,

    panelLeftOffset,
    PANEL_COLORS,
    sidebarColor,
    canAccessUserSettings,
    isSuperAdmin,

    fetchMarathiWordsList,
    toggleUserActive,
    fetchUsersList,
    fetchEnglishWords,
    handleOpenAddModal,
    handleCloseAddModal,
    handleActivationSubmit,
    handleConfirmDelete,
    autoSaveEnglishWord,
    handleDeleteExceptSelected,
    handlePrinterSetup,
  };
}

export default useSidebarDomain;
